<template>
    <div class="step-wrap">
        <div class="step-header">
            <h1 class="step-h1">Name it</h1>
            <p class="step-lead">This is what creators audition for. Make it specific.</p>
        </div>

        <div class="step-body hide-scrollbar">
            <!-- Suggestions -->
            <div class="suggestions-block">
                <div class="suggestions-row-header">
                    <p class="section-label">Need a title? Pick one</p>
                    <button class="shuffle-btn" @click="reshuffleSuggestions" aria-label="Shuffle suggestions">
                        <RefreshCw style="width:14px;height:14px" /> Shuffle
                    </button>
                </div>
                <div class="suggestions-pills">
                    <button
                        v-for="s in suggestions"
                        :key="s"
                        class="suggestion-pill"
                        :class="{ 'pill-active': title === s }"
                        @click="$emit('update:title', s)"
                    >{{ s }}</button>
                </div>
            </div>

            <!-- Custom input -->
            <div class="title-input-block">
                <p class="section-label section-label--own">Or type your own</p>
                <div class="title-input-wrap" :class="{ focused: inputActive }">
                    <input
                        type="text"
                        :value="title"
                        maxlength="60"
                        placeholder="Type your title…"
                        class="title-input"
                        @input="$emit('update:title', $event.target.value.slice(0, 60))"
                        @focus="onFocus"
                        @blur="onBlur"
                    />
                </div>
                <div class="hint-row">
                    <span :style="{ color: hintColor }">{{ hintText }}</span>
                    <span class="char-count">{{ title.length }}/60</span>
                </div>
            </div>
        </div>

        <div class="step-footer">
            <p v-if="error" class="error-msg">Your pitch needs a title. Tap a suggestion or type your own.</p>
            <button class="cta-btn" @click="handleNext">Tell my story</button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { generateUniqueTitles } from '@/Helpers/titleGenerator'
import { RefreshCw } from 'lucide-vue-next'

const props = defineProps({ title: String, settings: Array })
const emit = defineEmits(['update:title', 'next', 'update:inputFocused'])

const error      = ref(false)
const inputActive = ref(false)
const suggestions = ref(generateUniqueTitles(4, props.settings))

watch(() => props.title, v => { if (v.trim().length > 0) error.value = false })

function reshuffleSuggestions() {
    suggestions.value = generateUniqueTitles(4, props.settings)
}
function onFocus()  { emit('update:inputFocused', true);  inputActive.value = true  }
function onBlur()   { emit('update:inputFocused', false); inputActive.value = false }
function handleNext() {
    if (!props.title.trim()) { error.value = true } else { emit('next') }
}

const hintText = computed(() => {
    const l = props.title.length
    if (l === 0) return 'Aim for 3–7 words'
    if (l <= 45) return 'Good length'
    if (l <= 54) return 'Getting long…'
    if (l < 60)  return 'Almost at limit'
    return 'Max length'
})
const hintColor = computed(() => {
    const l = props.title.length
    if (l === 0) return 'rgba(255,255,255,0.4)'
    if (l <= 45) return 'rgba(74,222,128,0.7)'
    if (l <= 54) return 'rgba(251,191,36,0.7)'
    if (l < 60)  return 'rgba(251,191,36,0.9)'
    return 'rgba(248,113,113,0.9)'
})

</script>

<style scoped>
.step-wrap { display:flex; flex-direction:column; height:100%; padding:12px 16px 0; }
.step-header { margin-bottom:20px; flex-shrink:0; }
.step-h1 { font-size:32px; font-weight:800; color:white; margin-bottom:6px; }
.step-lead { color:rgba(255,255,255,0.7); font-size:15px; }
.step-body { flex:1; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; }
.suggestions-block { margin-bottom:40px; }
.suggestions-row-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
.section-label { font-size:11px; font-weight:700; color:rgba(255,255,255,0.5); text-transform:uppercase; letter-spacing:0.1em; }
.section-label--own { margin-bottom:0.625rem; }
.shuffle-btn { display:flex; align-items:center; gap:4px; color:#4D94FF; font-size:12px; font-weight:500; background:none; border:none; cursor:pointer; min-height:32px; padding:0 6px; }
.suggestions-pills { display:flex; flex-wrap:wrap; gap:8px; }
.suggestion-pill { padding:7px 14px; border-radius:999px; font-size:14px; border:1px solid rgba(255,255,255,0.1); background:rgba(255,255,255,0.05); color:rgba(255,255,255,0.8); cursor:pointer; transition:all 0.2s; }
.suggestion-pill:hover { background:rgba(0,82,204,0.2); border-color:rgba(0,82,204,0.4); color:white; }
.pill-active { background:rgba(0,82,204,0.2); border-color:rgba(0,82,204,0.5); color:white; }
.title-input-block { margin-bottom:16px; }
.title-input-wrap { border-radius:16px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); transition:border-color 0.2s; }
.title-input-wrap.focused { border-color:rgba(77,148,255,0.5); }
.title-input { width:100%; background:transparent; border:none; color:white; font-size:22px; font-weight:700; padding:14px 16px; outline:none; font-family:inherit; }
.title-input::placeholder { color:rgba(255,255,255,0.3); }
.hint-row { display:flex; justify-content:space-between; margin-top:6px; padding:0 4px; }
.char-count { font-size:11px; color:rgba(255,255,255,0.4); font-variant-numeric:tabular-nums; }
.step-footer { flex-shrink:0; padding:12px 0 4px; display:flex; flex-direction:column; gap:6px; }
.error-msg { color:#f87171; font-size:13px; text-align:center; font-weight:500; }
.cta-btn { width:100%; background:#0052CC; color:white; font-weight:700; font-size:17px; padding:14px 0; border-radius:16px; border:none; cursor:pointer; }
</style>
